import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import ImageUploader from '../../components/common/ImageUploader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiShoppingBag,
  FiStar,
  FiX
} from 'react-icons/fi';

const ProductsPage = () => {
  const { business } = useAuth();
  const { showToast } = useToast();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteProductTarget, setDeleteProductTarget] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    discountPrice: '',
    categoryId: '',
    image: '',
    featured: false,
    available: true
  });

  const fetchProducts = useCallback(async () => {
    try {
      let query = `/products?search=${encodeURIComponent(search)}`;
      if (selectedCategory) query += `&categoryId=${selectedCategory}`;
      const res = await api.get(query);
      if (res.success) {
        setProducts(res.data);
      }
    } catch (err) {
      showToast(err.message || 'Failed to fetch products', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, selectedCategory, showToast]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      if (res.success) {
        setCategories(res.data);
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts]);

  const handleOpenAddModal = () => {
    if (business?.plan === 'FREE' && products.length >= 5) {
      showToast('FREE plan allows up to 5 products. Upgrade to BASIC or PRO to add more!', 'error');
      return;
    }
    setEditingProduct(null);
    setForm({
      name: '',
      description: '',
      price: '',
      discountPrice: '',
      categoryId: categories.length > 0 ? categories[0]._id : '',
      image: '',
      featured: false,
      available: true
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (prod) => {
    setEditingProduct(prod);
    setForm({
      name: prod.name || '',
      description: prod.description || '',
      price: prod.price || '',
      discountPrice: prod.discountPrice || '',
      categoryId: prod.categoryId?._id || prod.categoryId || '',
      image: prod.image || '',
      featured: prod.featured || false,
      available: prod.available !== false
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || form.name.trim() === '') {
      showToast('Product name is required', 'error');
      return;
    }
    if (form.price === '' || Number(form.price) < 0) {
      showToast('Please enter a valid price', 'error');
      return;
    }

    setSubmitting(true);
    try {
      if (editingProduct) {
        const res = await api.put(`/products/${editingProduct._id}`, form);
        if (res.success) {
          showToast('Product updated successfully!', 'success');
        }
      } else {
        const res = await api.post('/products', form);
        if (res.success) {
          showToast('Product created successfully!', 'success');
        }
      }
      setIsModalOpen(false);
      fetchProducts();
    } catch (err) {
      showToast(err.message || 'Operation failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProductTarget) return;
    try {
      const res = await api.delete(`/products/${deleteProductTarget._id}`);
      if (res.success) {
        showToast('Product deleted successfully', 'success');
        setDeleteProductTarget(null);
        fetchProducts();
      }
    } catch (err) {
      showToast(err.message || 'Failed to delete product', 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', fontWeight: 800 }}>
            Products & Services
          </h1>
          <p style={{ color: '#94A3B8', fontSize: '0.95rem' }}>
            Manage your catalogue items, pricing, and availability. ({products.length} items)
          </p>
        </div>

        <button onClick={handleOpenAddModal} className="btn btn-primary">
          <FiPlus /> Add New Product
        </button>
      </div>

      {business?.plan === 'FREE' && (
        <div style={{ padding: '0.85rem 1.25rem', background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '12px', marginBottom: '1.5rem', fontSize: '0.9rem', color: '#FBBF24', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>FREE Plan limit: {products.length}/5 products used. Upgrade to Basic for unlimited products!</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '220px', position: 'relative' }}>
          <input
            type="text"
            className="form-control"
            style={{ paddingLeft: '2.5rem' }}
            placeholder="Search products by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FiSearch style={{ position: 'absolute', left: '12px', top: '12px', color: '#94A3B8' }} />
        </div>

        <select
          className="form-control"
          style={{ width: '220px' }}
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map((c) => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <LoadingSpinner text="Loading product catalogue..." />
      ) : products.length === 0 ? (
        <EmptyState
          title="No Products Found"
          description="You haven't added any products to your catalogue yet, or no items match your search."
          actionText="+ Add Your First Product"
          onAction={handleOpenAddModal}
          icon={FiShoppingBag}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
          {products.map((prod) => (
            <div key={prod._id} className="glass-card" style={{ padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {prod.image ? (
                <img src={prod.image} alt={prod.name} style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: '180px', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>
                  No Image Available
                </div>
              )}

              <div style={{ padding: '1.25rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#fff' }}>{prod.name}</h3>
                  {prod.featured && (
                    <span style={{ color: '#F59E0B' }} title="Featured Product">
                      <FiStar fill="#F59E0B" />
                    </span>
                  )}
                </div>

                {prod.categoryId && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--secondary-500)', fontWeight: '600', marginTop: '0.2rem' }}>
                    {prod.categoryId.name || 'Category'}
                  </span>
                )}

                {prod.description && (
                  <p style={{ color: '#94A3B8', fontSize: '0.85rem', margin: '0.5rem 0', flex: 1 }}>
                    {prod.description}
                  </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <div>
                    <span style={{ fontSize: '1.2rem', fontWeight: '800', color: '#fff' }}>
                      ₹{prod.discountPrice || prod.price}
                    </span>
                    {prod.discountPrice && (
                      <span style={{ fontSize: '0.85rem', color: '#94A3B8', textDecoration: 'line-through', marginLeft: '0.5rem' }}>
                        ₹{prod.price}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => handleOpenEditModal(prod)} className="btn btn-secondary btn-sm" title="Edit">
                      <FiEdit2 />
                    </button>
                    <button onClick={() => setDeleteProductTarget(prod)} className="btn btn-danger btn-sm" title="Delete">
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
              <button onClick={() => setIsModalOpen(false)} style={{ fontSize: '1.2rem', color: '#94A3B8' }}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <ImageUploader
                label="Product Image"
                value={form.image}
                onChange={(url) => setForm({ ...form, image: url })}
              />

              <div className="form-group">
                <label className="form-label">Product Name *</label>
                <input
                  type="text"
                  required
                  className="form-control"
                  placeholder="e.g. Pure Silk Saree"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Original Price (₹) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    className="form-control"
                    placeholder="999"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Discount Price (₹ Optional)</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    placeholder="799"
                    value={form.discountPrice}
                    onChange={(e) => setForm({ ...form, discountPrice: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-control"
                  value={form.categoryId}
                  onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  rows={3}
                  className="form-control"
                  placeholder="Describe the features or materials of this product..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: '1.5rem', margin: '1rem 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  />
                  Featured Product (HOT Badge)
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={form.available}
                    onChange={(e) => setForm({ ...form, available: e.target.checked })}
                  />
                  In Stock / Available
                </label>
              </div>

              <button type="submit" disabled={submitting} className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }}>
                {submitting ? 'Saving...' : editingProduct ? 'Update Product' : 'Save Product'}
              </button>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteProductTarget}
        title="Delete Product"
        message={`Are you sure you want to delete "${deleteProductTarget?.name}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteProductTarget(null)}
      />
    </div>
  );
};

export default ProductsPage;
